import Seo from "@/components/feature/Seo";
import Layout from "@/components/feature/Layout";
import ComicCard from "@/components/feature/ComicCard";
import { comics, categories } from "@/mocks/comics";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";

const statusFilters = [
  { key: "全部", label: "全部" },
  { key: "连载中", label: "连载中" },
  { key: "已完结", label: "已完结" },
  { key: "新上架", label: "新上架" },
] as const;

export default function Comics() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get("cat") || "全部";
  const initialQ = searchParams.get("q") || "";

  const [activeCat, setActiveCat] = useState(initialCat);
  const [status, setStatus] = useState<(typeof statusFilters)[number]["key"]>("全部");
  const [keyword, setKeyword] = useState(initialQ);

  const result = useMemo(() => {
    return comics.filter((c) => {
      const matchCat = activeCat === "全部" || c.categories.includes(activeCat);
      const matchStatus = status === "全部" || c.status === status;
      const kw = keyword.trim().toLowerCase();
      const matchKw =
        !kw ||
        c.title.toLowerCase().includes(kw) ||
        c.author.toLowerCase().includes(kw) ||
        c.tags.some((t) => t.toLowerCase().includes(kw));
      return matchCat && matchStatus && matchKw;
    });
  }, [activeCat, status, keyword]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "笨狗漫画网漫画分类列表",
    description: "浏览笨狗漫画网全部漫画，按连载、完结、新上架及类型分类，快速找到你想看的漫画。",
    url: "https://manhwanalysis.github.io/comics",
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: result.slice(0, 12).map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.title,
        url: `https://manhwanalysis.github.io/comic/${c.id}`,
      })),
    },
  };

  return (
    <Layout>
      <Seo
        title="漫画分类 | 笨狗漫画网连载、完结、新上架漫画大全"
        description="笨狗漫画网漫画分类，热血、奇幻、悬疑、科幻、萌系、武侠、都市、校园等类型一应俱全，支持连载、完结、新上架状态筛选与关键词搜索。"
        keywords="笨狗漫画网漫画分类,连载漫画,完结漫画,新上架漫画,热血漫画,奇幻漫画,悬疑漫画,科幻漫画"
        canonical="https://manhwanalysis.github.io/comics"
        jsonLd={jsonLd}
      />

      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <nav className="flex items-center gap-1 text-sm text-foreground-500">
            <button onClick={() => navigate("/")} className="hover:text-primary-600 cursor-pointer">
              首页
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900">漫画分类</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            漫画分类库
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            共收录 <span className="font-bold text-accent-600">{comics.length}</span> 部作品，按类型与状态浏览，快速找到你想看的漫画。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        {/* 搜索栏 */}
        <div className="mb-6 flex max-w-md items-center overflow-hidden rounded-full border border-background-300 bg-background-50 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索漫画标题、作者、标签…"
            className="w-full bg-transparent px-5 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          <button
            aria-label="搜索"
            className="mr-1 flex h-9 w-9 items-center justify-center rounded-full text-foreground-600 hover:text-primary-600 cursor-pointer"
          >
            <i className="ri-search-line"></i>
          </button>
        </div>

        {/* 状态筛选 */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {statusFilters.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatus(s.key)}
              className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                status === s.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* 分类筛选 */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setActiveCat("全部")}
            className={`rounded-full border px-4 py-1.5 text-sm whitespace-nowrap transition-colors cursor-pointer ${
              activeCat === "全部"
                ? "border-primary-500 bg-primary-500 text-background-50"
                : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400"
            }`}
          >
            全部类型
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCat(cat)}
              className={`rounded-full border px-4 py-1.5 text-sm whitespace-nowrap transition-colors cursor-pointer ${
                activeCat === cat
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between pb-4">
          <p className="text-sm text-foreground-500">
            找到 <span className="font-bold text-foreground-900">{result.length}</span> 部作品
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {result.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>

        {result.length === 0 && (
          <div className="py-20 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-500">没有找到符合条件的漫画，换个关键词试试吧</p>
          </div>
        )}
      </section>
    </Layout>
  );
}